import React from 'react';
import { BsExclamationTriangleFill } from 'react-icons/bs';

import { InvoiceWarning } from 'entities/Invoice';

import {
  ReadingDiscontinuityTable,
  readingDiscontinuityRows,
} from './reading-discontinuity-table.component';

const DISCONTINUITY_TEXT =
  'As leituras desta fatura não continuam de onde a anterior parou. Pode ser erro de leitura, troca de medidor não informada ou energia faturada fora do período. Entre em contato com a distribuidora.';

// Leitura descontínua: texto curto + tabela dos registradores (30/set/2026 —
// o texto corrido com três registradores era difícil de ler). Aviso antigo,
// sem `registers`, tem os números no próprio texto: vira a frase padrão.
const renderMessage = (w: InvoiceWarning, refMonth?: string) => {
  if (w.type !== 'meter_reading_discontinuity') return w.message;
  const hasRows = readingDiscontinuityRows(w).rows.length > 0;
  return (
    <>
      {w.registers?.length || !hasRows ? w.message : DISCONTINUITY_TEXT}
      <ReadingDiscontinuityTable warning={w} refMonth={refMonth} />
    </>
  );
};

interface Props {
  warnings?: InvoiceWarning[];
  // Mês da fatura (AAAA_MM) — cabeçalho da tabela de leituras
  refMonth?: string;
  className?: string;
}

/**
 * Banner de avisos de qualidade da fatura (leitura do medidor inconsistente,
 * leitura que não continua a fatura anterior, consumo muito acima da média).
 * Advisório — não bloqueia nada. Reutilizado no relatório, no modal de salvar
 * fatura e onde mais for necessário. Aviso `severity: 'critical'` (UC
 * descadastrada do SCEE) sai em vermelho, maior e com título — precisa chamar
 * atenção antes de qualquer outro número da fatura.
 */
export const InvoiceWarnings = ({ warnings, className, refMonth }: Props) => {
  if (!warnings || warnings.length === 0) return null;

  return (
    <div className={className}>
      {[...warnings]
        .sort(
          (a, b) =>
            Number(b.severity === 'critical') -
            Number(a.severity === 'critical')
        )
        .map((w, i) => {
          const critical = w.severity === 'critical';
          return (
            <div
              key={`${w.type}-${i}`}
              role={critical ? 'alert' : undefined}
              style={{
                display: 'flex',
                alignItems: 'flex-start',
                gap: critical ? 14 : 10,
                background: critical ? '#fef1f1' : '#fef4e9',
                border: critical ? '2px solid #f42121' : '1px solid #F79422',
                borderRadius: 3,
                padding: critical ? '18px 20px' : '12px 16px',
                marginBottom: critical ? 12 : 8,
                fontFamily: 'Montserrat',
                fontSize: critical ? 15 : 14,
                lineHeight: critical ? '22px' : '20px',
                color: critical ? '#c21b1b' : '#333333',
              }}
            >
              <BsExclamationTriangleFill
                color={critical ? '#f42121' : '#F79422'}
                size={critical ? 26 : 18}
                style={{ flexShrink: 0, marginTop: 2 }}
              />
              <span style={{ flex: 1 }}>
                {(w.title || w.type === 'meter_reading_discontinuity') && (
                  <strong
                    style={{
                      display: 'block',
                      fontSize: critical ? 17 : 14,
                      marginBottom: 4,
                    }}
                  >
                    {w.title || 'Leitura não continua a fatura anterior'}
                  </strong>
                )}
                {renderMessage(w, refMonth)}
              </span>
            </div>
          );
        })}
    </div>
  );
};
